import React, { useMemo, useState } from 'react';
import { Input } from 'reactstrap';
import { currencyFormatter } from '../../utils/currencyFormatter';
import { notifyError } from '../../utils/notify';

const TabelaEspacamentoPropItensTemporalEmMeses = ({
  linhas = [],
  chaveCabecalhoLinhas = '',
  chaveAgrupamento = 'propItemGr_desc',
  chaveValor = 'propOrcItem_valor',
  setValoresPorLinhaEColuna,
  valoresPorLinhaEColuna,
  setQuantidadeColunas,
  quantidadeColunas,
  chaveIdentificador,
  handleChangeQuantidadeColunas
}) => {
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const [botaoHoverKey, setBotaoHoverKey] = useState('');

  const getLinhaKey = (linha, indiceLinha) => linha?.id ?? linha[chaveIdentificador] ?? indiceLinha;

  const indicesColunas = useMemo(
    () => Array.from({ length: quantidadeColunas }, (_, index) => index + 1),
    [quantidadeColunas]
  );

  const grupos = useMemo(() => {
    const mapaGrupos = {};

    linhas.forEach((linha, indiceLinha) => {
      const nomeGrupo = linha?.[chaveAgrupamento] ?? 'Sem grupo';

      if (!mapaGrupos[nomeGrupo]) {
        mapaGrupos[nomeGrupo] = [];
      }

      mapaGrupos[nomeGrupo].push({ linha, indiceLinha });
    });

    return Object.entries(mapaGrupos).map(([nomeGrupo, itens]) => ({
      nomeGrupo,
      itens,
    }));
  }, [linhas, chaveAgrupamento]);

  const handleChangeValorCelula = (linha, indiceLinha, indiceColuna, valorInput) => {
    const linhaKey = getLinhaKey(linha, indiceLinha);

    if (valorInput === '') {
      setValoresPorLinhaEColuna((prev) => {
        const valoresLinha = { ...(prev[linhaKey] || {}) };
        delete valoresLinha[indiceColuna];

        if (Object.keys(valoresLinha).length === 0) {
          const novoEstado = { ...prev };
          delete novoEstado[linhaKey];
          return novoEstado;
        }

        return {
          ...prev,
          [linhaKey]: valoresLinha,
        };
      });
      return;
    }

    const inteiro = Math.max(0, Math.floor(Number(valorInput)));
    const valorFinal = Number.isNaN(inteiro) ? 0 : inteiro;
    const totalDisponivel = Number(linha?.propOrcItem_qtd || 0);
    const valorAtualCelula = Number(valoresPorLinhaEColuna[linhaKey]?.[indiceColuna] || 0);
    const totalAlocadoAtual = getTotalAlocadoDaLinha(linhaKey);
    const novoTotalAlocado = totalAlocadoAtual - valorAtualCelula + valorFinal;

    if (novoTotalAlocado > totalDisponivel) {
      notifyError(
        `A alocacao total de "${linha?.[chaveCabecalhoLinhas] ?? 'item orcamentario'}" nao pode ultrapassar ${totalDisponivel}.`
      );
      return;
    }

    setValoresPorLinhaEColuna((prev) => ({
      ...prev,
      [linhaKey]: {
        ...(prev[linhaKey] || {}),
        [indiceColuna]: valorFinal,
      },
    }));
  };

  const handleDistribuirMensalmente = (linha, indiceLinha) => {
    const linhaKey = getLinhaKey(linha, indiceLinha);
    const totalDisponivel = Math.max(0, Math.floor(Number(linha?.propOrcItem_qtd || 0)));
    const totalMeses = indicesColunas.length;

    if (totalMeses <= 0) {
      return;
    }

    const base = Math.floor(totalDisponivel / totalMeses);
    let restante = totalDisponivel % totalMeses;
    const novaDistribuicao = {};

    indicesColunas.forEach((indiceColuna) => {
      const adicional = restante > 0 ? 1 : 0;
      novaDistribuicao[indiceColuna] = base + adicional;
      restante -= adicional;
    });

    setValoresPorLinhaEColuna((prev) => ({
      ...prev,
      [linhaKey]: novaDistribuicao,
    }));
  };

  const handleZerarDistribuicao = (linha, indiceLinha) => {
    const linhaKey = getLinhaKey(linha, indiceLinha);
    const novaDistribuicao = {};

    indicesColunas.forEach((indiceColuna) => {
      novaDistribuicao[indiceColuna] = 0;
    });

    setValoresPorLinhaEColuna((prev) => ({
      ...prev,
      [linhaKey]: novaDistribuicao,
    }));
  };

  const toggleGrupo = (nomeGrupo) => {
    setGruposExpandidos((prev) => ({
      ...prev,
      [nomeGrupo]: !prev[nomeGrupo],
    }));
  };

  const getTotalAlocadoDaLinha = (linhaKey) => {
    const valoresDaLinha = valoresPorLinhaEColuna[linhaKey] || {};
    return Object.values(valoresDaLinha).reduce((soma, valor) => soma + Number(valor || 0), 0);
  };

  const getTotalDisponivelDaLinha = (linha) => {
    return Number(linha?.propOrcItem_qtd || 0);
  };

  const getValorTotalAlocadoDaLinha = (linha, linhaKey) => {
    const totalQuantidade = getTotalAlocadoDaLinha(linhaKey);
    const valorUnitario = Number(linha[chaveValor] || 0);
    return totalQuantidade * valorUnitario;
  };

  const getValorAlocadoDaCelula = (linha, linhaKey, indiceColuna) => {
    const quantidade = Number(valoresPorLinhaEColuna[linhaKey]?.[indiceColuna] || 0);
    const valorUnitario = Number(linha[chaveValor] || 0);
    return quantidade * valorUnitario;
  };

  const getValorTotalDaColuna = (indiceColuna) => {
    return linhas.reduce((soma, linha, indiceLinha) => {
      const linhaKey = getLinhaKey(linha, indiceLinha);
      const quantidade = Number(valoresPorLinhaEColuna[linhaKey]?.[indiceColuna] || 0);
      const valorUnitario = Number(linha[chaveValor] || 0);
      return soma + (quantidade * valorUnitario);
    }, 0);
  };

  const getTotalQuantidadeDoGrupo = (itens) => {
    return itens.reduce((soma, { linha, indiceLinha }) => {
      const linhaKey = getLinhaKey(linha, indiceLinha);
      return soma + getTotalAlocadoDaLinha(linhaKey);
    }, 0);
  };

  const getTotalValorDoGrupo = (itens) => {
    return itens.reduce((soma, { linha, indiceLinha }) => {
      const linhaKey = getLinhaKey(linha, indiceLinha);
      return soma + getValorTotalAlocadoDaLinha(linha, linhaKey);
    }, 0);
  };

  const stickyPrimeiraColunaStyle = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    backgroundColor: '#fff',
  };

  const stickyPrimeiraColunaAgrupamentoStyle = {
    ...stickyPrimeiraColunaStyle,
    backgroundColor: '#f8f9fa',
    zIndex: 3,
  };

  const acaoLinhaStyle = {
    border: '1px solid #0d6efd',
    borderRadius: 4,
    backgroundColor: '#fff',
    color: '#0d6efd',
    fontSize: 11,
    padding: '2px 6px',
    lineHeight: 1.3,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const getAcaoLinhaStyle = (botaoKey) => ({
    ...acaoLinhaStyle,
    backgroundColor: botaoHoverKey === botaoKey ? '#0d6efd' : '#fff',
    color: botaoHoverKey === botaoKey ? '#fff' : '#0d6efd',
  });

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            />
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'center',
                minWidth: '120px',
              }}
            >
              Total alocado
            </th>
            {indicesColunas.map((indiceColuna) => (
              <th
                key={indiceColuna}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {indiceColuna}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grupos.map(({ nomeGrupo, itens }) => {
            const grupoExpandido = !!gruposExpandidos[nomeGrupo];

            return (
              <React.Fragment key={nomeGrupo}>
                <tr>
                  <th
                    onClick={() => toggleGrupo(nomeGrupo)}
                    style={{
                      border: '1px solid #ced4da',
                      padding: '6px 8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      backgroundColor: '#f8f9fa',
                      minWidth: '260px',
                      ...stickyPrimeiraColunaAgrupamentoStyle,
                    }}
                  >
                    {grupoExpandido ? 'v' : '>'} {nomeGrupo}
                  </th>
                  <td
                    style={{
                      minWidth: '120px',
                      border: '1px solid #ced4da',
                      backgroundColor: '#f8f9fa',
                      textAlign: 'center',
                    }}
                  >
                    {/* <div>{getTotalQuantidadeDoGrupo(itens)}</div> */}
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                      {currencyFormatter.format(getTotalValorDoGrupo(itens))}
                    </div>
                  </td>
                  {indicesColunas.map((indiceColuna) => (
                    <td
                      key={`${nomeGrupo}-${indiceColuna}`}
                      style={{ minWidth: '90px', border: '1px solid #ced4da', backgroundColor: '#f8f9fa' }}
                    />
                  ))}
                </tr>

                {grupoExpandido &&
                  itens.map(({ linha, indiceLinha }) => {
                    const linhaKey = getLinhaKey(linha, indiceLinha);

                    return (
                      <tr key={`${nomeGrupo}-${linhaKey}`}>
                        <th
                          style={{
                            border: '1px solid #ced4da',
                            padding: '6px 8px 6px 20px',
                            textAlign: 'left',
                            fontWeight: 400,
                            minWidth: '260px',
                            ...stickyPrimeiraColunaStyle,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 8,
                            }}
                          >
                            <span>{linha?.[chaveCabecalhoLinhas] ?? ''}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <button
                                type="button"
                                onClick={() => handleDistribuirMensalmente(linha, indiceLinha)}
                                onMouseEnter={() => setBotaoHoverKey(`${linhaKey}-distribuir`)}
                                onMouseLeave={() => setBotaoHoverKey('')}
                                style={getAcaoLinhaStyle(`${linhaKey}-distribuir`)}
                              >
                                distribuir mensalmente
                              </button>
                              <button
                                type="button"
                                onClick={() => handleZerarDistribuicao(linha, indiceLinha)}
                                onMouseEnter={() => setBotaoHoverKey(`${linhaKey}-zerar`)}
                                onMouseLeave={() => setBotaoHoverKey('')}
                                style={getAcaoLinhaStyle(`${linhaKey}-zerar`)}
                              >
                                zerar distribuicao
                              </button>
                            </div>
                          </div>
                        </th>
                        <td style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}>
                          <div>{`${getTotalAlocadoDaLinha(linhaKey)}/${getTotalDisponivelDaLinha(linha)}`}</div>
                          <div style={{ marginTop: 4, fontSize: 12 }}>
                            {currencyFormatter.format(getValorTotalAlocadoDaLinha(linha, linhaKey))}
                          </div>
                        </td>
                        {indicesColunas.map((indiceColuna) => (
                          <td
                            key={`${indiceLinha}-${indiceColuna}`}
                            style={{ border: '1px solid #ced4da', padding: '6px 8px' }}
                          >
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={valoresPorLinhaEColuna[linhaKey]?.[indiceColuna] ?? ''}
                              style={{ width: '4rem', margin: '0 auto' }}
                              onWheel={(e) => {
                                e.target.blur();
                              }}
                              onChange={(event) =>
                                handleChangeValorCelula(linha, indiceLinha, indiceColuna, event.target.value)
                              }
                            />
                            <div style={{ marginTop: 4, fontSize: 12, textAlign: 'center' }}>
                              {currencyFormatter.format(getValorAlocadoDaCelula(linha, linhaKey, indiceColuna))}
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          })}
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                backgroundColor: '#f8f9fa',
                minWidth: '260px',
                ...stickyPrimeiraColunaAgrupamentoStyle,
              }}
            >
              Total em cada coluna
            </th>
            <td style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa' }} />
            {indicesColunas.map((indiceColuna) => (
              <td
                key={`total-coluna-${indiceColuna}`}
                style={{
                  border: '1px solid #ced4da',
                  padding: '6px 8px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  fontWeight: 600,
                }}
              >
                {currencyFormatter.format(getValorTotalDaColuna(indiceColuna))}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default TabelaEspacamentoPropItensTemporalEmMeses;
